/**
 * 后台上传队列（持久化）—— 原图补传专用，2026-10-20。
 *
 * 需求来源（用户）：「后台上传应该退出页面也能继续…只要不关闭项目吧…」
 *
 * 为什么需要它（原来的实现有什么问题）：
 *   上一个版本把「原图后台补传」写成了一个内存里的 Promise：`uploadChunked` 里
 *   `sendAll(...).then(link)`。它有两个致命短板，用户一退出页面就暴露：
 *     ① **组件卸载会中断**：`SitePhotosCard` 的 `useEffect(() => () => abort())`
 *        在离开页面时把 AbortController 一 abort，正在跑的后台补传直接被掐断；
 *     ② **刷新 / 关标签就丢失**：Promise 只活在内存里，File 对象也在内存里。
 *        用户传完预览、换个页面回来看，原图永远补不上，而且**没有任何提示**。
 *
 * 这套队列的做法：
 *   · 原图（File/Blob）存进 **IndexedDB**（不是 localStorage —— 那是同步 API，
 *     存 10MB 二进制会卡主线程，而且有 5MB 上限直接失败）；
 *   · 每一件任务记录「源文件 blob + 要挂到哪个预览 fileId 上 + 重试次数」，
 *     存进去之后由全局 worker（uploadRunner.js）取出来跑，与组件生命周期解耦；
 *   · 跑完（成功或彻底失败）从库里删掉，因此**不会无限膨胀**；
 *   · 刷新页面 / 重新登录后 worker 会重新扫库续传 —— 这就是「退出页面也能继续」。
 *
 * 为什么不用 Service Worker Background Sync：
 *   它能"关掉标签页还继续"，但需要注册 SW + 处理 push 权限，且 iOS Safari 至今
 *   不支持 Background Sync API。本项目的用户大量用手机拍照上传，做一半不如不做。
 *   当前口径是「**不关闭/刷新整个页面就能续**」：切菜单、换项目、离开这个卡片都不中断。
 *   真正的"关掉浏览器也能传"要等有后台同步能力再说，这里不做假承诺。
 */

const DB_NAME = "aligcus_upload";
const DB_VERSION = 1;
const STORE = "originals";

function openDb() {
  return new Promise((res, rej) => {
    if (typeof indexedDB === "undefined") { rej(new Error("IndexedDB unavailable")); return; }
    const r = indexedDB.open(DB_NAME, DB_VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "id" });
        // 按入队时间取任务：FIFO，先传的先补
        os.createIndex("at", "at");
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

const tx = (db, mode) => db.transaction(STORE, mode).objectStore(STORE);

/**
 * 入队后的「叫醒」钩子。
 *
 * 为什么用回调注入而不是直接 import uploadRunner：
 *   `upload.js` 需要入队，`uploadRunner.js` 需要调用 `upload.js` 的上传函数。
 *   两边互相 import 会形成循环依赖 —— ESM 虽然能靠函数提升勉强跑通，但一旦以后
 *   有人在模块顶层用到对方的常量，就会拿到 undefined 并在生产环境炸得莫名其妙。
 *   这里反过来让 runner 在启动时把钩子注册进来，依赖方向就只剩
 *   `upload.js → uploadQueue.js ← uploadRunner.js → upload.js` 的**单向链**。
 */
let onEnqueue = null;
export const setEnqueueHook = (fn) => { onEnqueue = typeof fn === "function" ? fn : null; };

/** 入队一件原图补传任务。id 用「预览 fileId」——同一条预览只可能有一个源文件，天然幂等。 */
export async function enqueueOriginal({ previewFileId, blob, filename, contentType, username, lastModified }) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, "readwrite");
    t.objectStore(STORE).put({
      id: previewFileId,
      previewFileId,
      blob,
      filename: filename || "original",
      contentType: contentType || "application/octet-stream",
      username: username || "",
      // 原文件的修改时间：worker 重建 File 时**必须**用它，断点续传的 key 才稳定
      // （upload.js 的 fileKey = 名字:大小:lastModified，写 Date.now() 会让每次都从第 0 片重传）
      lastModified: lastModified || blob?.lastModified || Date.now(),
      at: Date.now(),
      tries: 0,
    });
    t.oncomplete = () => {
      db.close();
      try { onEnqueue?.(); } catch { /* 钩子异常不能影响入队结果 */ }
      res();
    };
    t.onerror = () => { db.close(); rej(t.error); };
  });
}

/** 取最早入队的一条任务（没有则 null）。
 *
 *  username 过滤：浏览器是多账号共用的（门店 / 办公室同一台机器换人登录很常见）。
 *  若不过滤，A 留下的原图任务会被 B 的 token 拿去传 —— 后端 `/files/{fid}/original`
 *  校验文件归属会返回 403，worker 按「4xx 不重试」把任务丢掉，A 回来时源文件就永久没了。
 *  传入当前用户名后，只处理自己的任务，别人的任务原地留着等他自己登录。
 */
export async function peekOriginal(username) {
  const db = await openDb();
  return new Promise((res, rej) => {
    // ⚠️ 用 getAll 排序取第一条，而不是 index.openCursor()。
    // 原因（2026-10-20 实测踩到）：游标在 onsuccess 里直接 db.close()、又没调
    // cursor.continue() 时，事务可能卡在 pending，回调就不再触发 —— 表现为
    // window.__uploadRunner 里 running=true 但 lastPeek 永不更新，任务一直躺着没人处理。
    // getAll 一次性读到底，回调必然触发，行为稳定。
    // 队列里同时最多几十条，排序开销可忽略；不过**只取元数据**，不把 blob 读进内存
    // （用 getAll 会连 blob 一起读，这里体量小可接受；真要优化可改成读 id 再单取）。
    const rq = db.transaction(STORE).objectStore(STORE).getAll();
    rq.onsuccess = () => {
      const rows = (rq.result || [])
        .filter((x) => !username || !x.username || x.username === username)
        .sort((a, b) => (a.at || 0) - (b.at || 0));
      db.close();
      res(rows[0] || null);
    };
    rq.onerror = () => { db.close(); rej(rq.error); };
  });
}

/** 全部任务（给「待补传 N 项」这类界面提示用）。 */
export async function listOriginals() {
  const db = await openDb();
  return new Promise((res, rej) => {
    const rq = db.transaction(STORE).objectStore(STORE).getAll();
    rq.onsuccess = () => { db.close(); res(rq.result || []); };
    rq.onerror = () => { db.close(); rej(rq.error); };
  });
}

export async function removeOriginal(id) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, "readwrite");
    t.objectStore(STORE).delete(id);
    t.oncomplete = () => { db.close(); res(); };
    t.onerror = () => { db.close(); rej(t.error); };
  });
}

/** 记一次失败。超过 maxTries 由调用方决定丢弃（见 uploadRunner）。 */
export async function bumpOriginalTries(id, tries) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, "readwrite");
    const os = t.objectStore(STORE);
    const rq = os.get(id);
    rq.onsuccess = () => {
      const rec = rq.result;
      if (rec) { rec.tries = tries; rec.lastTryAt = Date.now(); os.put(rec); }
    };
    t.oncomplete = () => { db.close(); res(); };
    t.onerror = () => { db.close(); rej(t.error); };
  });
}
